import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Archive, Megaphone, Pencil, Plus, Send } from "lucide-react";
import { Button } from "../../ui/Button";
import { Badge } from "../../ui/Badge";
import { EmptyState } from "../../ui/EmptyState";
import { Modal } from "../../ui/Modal";
import { cn } from "../../../utils/cn";
import { archiveAnnouncement, listAnnouncements } from "../../../api/announcements";
import AnnouncementEditorModal from "./AnnouncementEditorModal";
import { displayStatus } from "./announcementForm";

// ─────────────────────────────────────────────────────────────────────────────
// Panel de pop-ups para comercios. Lo usan:
//   - el operador (admin / superadmin logístico) en /admin/notifications,
//     acotado a SU operador (el backend lo fuerza desde el token);
//   - el superadmin del sistema en /plataforma, con los de Beartrack y los de
//     todos los operadores (`systemMode`).
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { value: "", label: "Todos" },
  { value: "draft", label: "Borradores" },
  { value: "published", label: "Publicados" },
  { value: "archived", label: "Archivados" },
];

const STATUS_BADGE = {
  draft: { tone: "neutral", label: "Borrador" },
  scheduled: { tone: "info", label: "Programado" },
  live: { tone: "success", label: "Publicado" },
  expired: { tone: "warning", label: "Vencido" },
  archived: { tone: "muted", label: "Archivado" },
};

function formatDate(iso, locale) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

export function audienceSummary(audience, t) {
  if (!audience || audience.mode !== "filtered") {
    return audience?.operatorIds?.length
      ? t("Todos los comercios de {{n}} operadores", { n: audience.operatorIds.length })
      : t("Todos los comercios");
  }
  const parts = [];
  if (audience.integrations?.length) {
    parts.push(audience.integrations.map((i) => i.label).join(", "));
  }
  if (audience.communes?.length) {
    parts.push(
      audience.communes.length <= 3
        ? audience.communes.map((c) => c.name).join(", ")
        : t("{{n}} comunas", { n: audience.communes.length }),
    );
  }
  return parts.join(audience.match === "any" ? ` ${t("o")} ` : ` ${t("y")} `);
}

/**
 * @param {{ systemMode: boolean, operators?: {id,name,logo}[],
 *   ownOperator?: {id,name,logo}, operatorCountryCode: string }} props
 */
export default function AnnouncementsAdminPanel({
  systemMode = false,
  operators = [],
  ownOperator,
  operatorCountryCode,
}) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editor, setEditor] = useState(null); // { announcement: object|null }
  const [archiving, setArchiving] = useState(null);
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const result = await listAnnouncements({
      page,
      limit: PAGE_SIZE,
      status: status || undefined,
    });
    setLoading(false);
    if (!result.ok) {
      setError(t(result.error));
      return;
    }
    setData({ items: result.data?.items ?? [], total: result.data?.total ?? 0 });
  }, [page, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const operatorName = useMemo(() => {
    const byId = new Map(operators.map((o) => [o.id, o.name]));
    return (announcement) =>
      announcement.scope === "platform"
        ? "Beartrack"
        : announcement.operator?.name || byId.get(announcement.operator?.id) || t("Operador");
  }, [operators, t]);

  const runAction = async (id, action) => {
    setActionError("");
    setBusyId(id);
    const result = await action(id);
    setBusyId(null);
    if (!result.ok) {
      setActionError(t(result.error));
      return false;
    }
    await load();
    return true;
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const now = new Date();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label={t("Filtrar por estado")} className="inline-flex flex-wrap rounded-lg border border-border p-0.5">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value || "all"}
              type="button"
              role="tab"
              aria-selected={status === filter.value}
              onClick={() => {
                setStatus(filter.value);
                setPage(1);
              }}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                status === filter.value ? "bg-bg-muted text-fg" : "text-fg-muted hover:text-fg",
              )}
            >
              {t(filter.label)}
            </button>
          ))}
        </div>
        <Button
          onClick={() => setEditor({ announcement: null })}
          iconLeft={<Plus className="h-4 w-4" aria-hidden="true" />}
        >
          {t("Nuevo pop-up")}
        </Button>
      </div>

      {actionError && (
        <div role="alert" className="rounded-lg bg-status-error-soft px-3 py-2 text-sm text-status-error">
          {actionError}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg border border-border bg-bg-muted" />
          ))}
        </div>
      ) : error ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg bg-status-error-soft px-3 py-2 text-sm text-status-error">
          <span className="mr-auto">{error}</span>
          <Button size="sm" variant="outline" onClick={load}>
            {t("Reintentar")}
          </Button>
        </div>
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={<Megaphone />}
          title={status ? t("No hay pop-ups en este estado") : t("Todavía no hay pop-ups")}
          description={t(
            "Crea un pop-up con texto, imágenes y links para todos tus comercios o solo para los que tengan cierta integración o estén en ciertas comunas.",
          )}
          action={
            <Button onClick={() => setEditor({ announcement: null })} iconLeft={<Plus className="h-4 w-4" aria-hidden="true" />}>
              {t("Nuevo pop-up")}
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {data.items.map((announcement) => {
            const visible = displayStatus(announcement, now);
            const badge = STATUS_BADGE[visible];
            const archived = announcement.status === "archived";
            return (
              <li
                key={announcement.id}
                className="flex flex-col gap-3 rounded-lg border border-border bg-bg p-4 sm:flex-row sm:items-center"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={badge.tone} dot>
                      {t(badge.label)}
                    </Badge>
                    {systemMode && (
                      <span className="text-xs font-medium text-fg-muted">{operatorName(announcement)}</span>
                    )}
                  </div>
                  <p className="truncate text-sm font-semibold text-fg">{announcement.title}</p>
                  <p className="truncate text-xs text-fg-muted">{audienceSummary(announcement.audience, t)}</p>
                  <p className="text-xs text-fg-muted">
                    {announcement.endsAt
                      ? t("Del {{from}} al {{to}}", {
                          from: formatDate(announcement.startsAt, i18n.language),
                          to: formatDate(announcement.endsAt, i18n.language),
                        })
                      : t("Desde el {{from}}, sin término", {
                          from: formatDate(announcement.startsAt, i18n.language),
                        })}
                  </p>
                </div>

                <dl className="flex gap-4 text-center">
                  {[
                    ["seen", t("Vistos")],
                    ["dismissed", t("Descartados")],
                    ["ctaClicked", t("Clics")],
                  ].map(([key, label]) => (
                    <div key={key} className="flex flex-col">
                      <dt className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{label}</dt>
                      <dd className="text-sm font-semibold tabular-nums text-fg">
                        {announcement.stats?.[key] ?? 0}
                      </dd>
                    </div>
                  ))}
                </dl>

                {!archived && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditor({ announcement })}
                      iconLeft={<Pencil className="h-4 w-4" aria-hidden="true" />}
                    >
                      {t("Editar")}
                    </Button>
                    {/* Publicar abre el editor: siempre pasa por la confirmación con el alcance de hoy. */}
                    {announcement.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => setEditor({ announcement })}
                        iconLeft={<Send className="h-4 w-4" aria-hidden="true" />}
                        disabled={busyId === announcement.id}
                      >
                        {t("Publicar")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setArchiving(announcement)}
                      iconLeft={<Archive className="h-4 w-4" aria-hidden="true" />}
                      disabled={busyId === announcement.id}
                    >
                      {t("Archivar")}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm text-fg-muted">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t("Anterior")}
          </Button>
          <span className="tabular-nums">
            {page}/{totalPages}
          </span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            {t("Siguiente")}
          </Button>
        </div>
      )}

      {editor && (
        <AnnouncementEditorModal
          open
          announcement={editor.announcement}
          onClose={() => setEditor(null)}
          onSaved={load}
          systemMode={systemMode}
          operators={operators}
          ownOperator={ownOperator}
          operatorCountryCode={operatorCountryCode}
        />
      )}

      <Modal
        open={Boolean(archiving)}
        onClose={() => setArchiving(null)}
        size="sm"
        title={t("¿Archivar este pop-up?")}
        description={t("Deja de mostrarse de inmediato y no se puede volver a publicar.")}
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button variant="ghost" onClick={() => setArchiving(null)}>
              {t("Cancelar")}
            </Button>
            <Button
              variant="danger"
              loading={busyId === archiving?.id}
              onClick={async () => {
                const ok = await runAction(archiving.id, archiveAnnouncement);
                if (ok) setArchiving(null);
              }}
            >
              {t("Archivar")}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-fg">{archiving?.title}</p>
      </Modal>
    </div>
  );
}
